import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useDuplicateTask } from '../../hooks/useTasks';
import type { Task } from '../../lib/tasksApi';
import { ProjectSelect } from './ProjectSelect';

interface Props {
  task: Task;
  onClose: () => void;
  /** Called with the new task once it exists; the page navigates to it. */
  onDuplicated: (copy: Task) => void;
}

/**
 * "Duplică sarcina" — the ⋯ menu's copy action. The copy keeps everything
 * that describes the work (description, status, priority, assignee, due date,
 * estimate, labels, department, service) and gets a fresh identifier in the
 * chosen project, which defaults to the task's own. Sub-issues come along
 * only when asked; comments, worklog and relations never do.
 */
export function DuplicateTaskModal({ task, onClose, onDuplicated }: Props) {
  const { t } = useI18n();
  const duplicate = useDuplicateTask();
  const [title, setTitle] = useState(t('tasks.duplicate.defaultTitle', { title: task.title }));
  const [projectId, setProjectId] = useState(task.projectId);
  const [includeSubtasks, setIncludeSubtasks] = useState(task.subIssueCount > 0);

  function submit() {
    const trimmed = title.trim();
    if (!trimmed || duplicate.isPending) return;
    duplicate.mutate(
      {
        id: task.id,
        input: {
          title: trimmed,
          projectId: projectId !== task.projectId ? projectId : undefined,
          includeSubtasks: task.subIssueCount > 0 && includeSubtasks,
        },
      },
      { onSuccess: (copy) => onDuplicated(copy) },
    );
  }

  return (
    <div className="ui-modal-backdrop" onClick={onClose}>
      <div className="ui-modal-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900">{t('tasks.duplicate.title')}</h2>
        <p className="mt-1 text-sm text-gray-500">
          {t('tasks.duplicate.hint', { identifier: task.identifier ?? task.title })}
        </p>

        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="block">
            <span className="ui-field-label">{t('tasks.duplicate.newTitle')}</span>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="ui-input mt-1 w-full"
            />
          </label>

          <div>
            <span className="ui-field-label">{t('tasks.duplicate.project')}</span>
            <div className="mt-1">
              <ProjectSelect value={projectId} onChange={setProjectId} width={360} />
            </div>
            {projectId !== task.projectId && (
              <p className="mt-1 text-xs text-gray-500">{t('tasks.duplicate.otherProject')}</p>
            )}
          </div>

          {task.subIssueCount > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeSubtasks}
                onChange={(e) => setIncludeSubtasks(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-600"
              />
              {t('tasks.duplicate.includeSubtasks', { count: task.subIssueCount })}
            </label>
          )}

          {duplicate.isError && (
            <p className="text-sm text-red-600">{t('tasks.duplicate.failed')}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!title.trim() || duplicate.isPending}
              className="ui-btn-primary"
            >
              {duplicate.isPending ? t('tasks.duplicate.working') : t('tasks.duplicate.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
